<template>
  <div class="time-off-policy-setup">
    <!-- eslint-disable vue/no-mutating-props -->
    <div class="mb-15">
      <ValidationProvider
        v-slot="{ errors }"
        mode="eager"
        rules="required|min:3|max:50"
        name="name"
      >
        <div :class="{ 'has-error': errors[0] }">
          <label class="control-label required">Policy name*</label>
          <AppInput
            v-model="timeOffPolicy.name"
            placeholder="Enter the policy name"
            data-testid="policy-name-input"
          />
          <span class="help-block">{{ errors[0] }}</span>
        </div>
      </ValidationProvider>
    </div>
    <div class="mb-15">
      <ValidationProvider
        v-slot="{ errors }"
        mode="eager"
        rules="required"
        name="accrual_type"
        :model-value="timeOffPolicy.accrual_type"
      >
        <div :class="{ 'has-error': errors[0] }">
          <label class="control-label required">Schedule of accrual</label>
          <i
            v-tooltip="tooltipOptions"
            :title="tooltipText"
            class="hi hi-info-filled hi-12 hi-tooltip text-grey ml-10"
          />
          <div v-show="editing">
            {{ accrualType }}
            <p class="accrual-policy-change-notice">Updates to accrual settings are not retroactive</p>
          </div>
          <AppSelect
            v-show="!editing"
            v-model="timeOffPolicy.accrual_type"
            :options="dropdownOptions.accrual_type"
            :select2-options="select2Options"
            @input="setSubfieldsVisibility"
          />
          <span class="help-block">{{ errors[0] }}</span>
        </div>

        <div
          v-if="hasAccrualScheduleWarning"
          class="seats-increase-warning-box dialog-warning blue"
          style="align-items: center;"
        >
          <i class="d-flex hi hi-info-filled hi-12 text-blue">
          </i>
          <div v-if="accrualTypeId === 1">
            Time off hours accrue only when hours are <strong>marked as paid</strong> —
            they’re included in a payment record, not necessarily paid out.
            <a
              href="https://support.hubstaff.com/paid-time-off-holidays-and-time-off-requests/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more
              <i class="hi-external hi-12 text-blue"></i>
            </a>
          </div>
          <div v-if="accrualTypeId === 3">
            Balances are prorated and may take up to <strong>24 hours</strong> to display for members added to
            a time off policy set to “Policy joined date”.
          </div>
        </div>
      </ValidationProvider>
    </div>
    <div
      v-if="showAmountAccrued"
      class="mb-8"
    >
      <ValidationProvider
        v-slot="{ errors }"
        mode="eager"
        rules="required"
        name="hours_per"
      >
        <div :class="{ 'has-error': errors[0] }">
          <label class="control-label required">Amount accrued*</label>
          <div class="row">
            <div class="col-md-6 pr-lg-0">
              <div class="d-flex input-group">
                <AppInput v-model.number="timeOffPolicy.accrual_policy.hours_per" />
                <span class="input-group-addon corner-md-r no-wrap">hour(s) accrued for every</span>
              </div>
              <span class="help-block">{{ errors[0] }}</span>
            </div>
            <div class="col-md-6 pl-lg-0">
              <div class="d-flex input-group corner-md-l">
                <AppInput v-model.number="timeOffPolicy.accrual_policy.hours_worked" />
                <span class="input-group-addon no-wrap">hours worked</span>
              </div>
              <span class="help-block">{{ errors[0] }}</span>
            </div>
          </div>
        </div>
      </ValidationProvider>
    </div>
    <div
      v-if="timeOffPolicy.accrual_type === '0'"
      class="row"
    >
      <div class="col-md-12">
        <ValidationProvider
          v-slot="{ errors }"
          mode="eager"
          :rules="betweenOrNullRule"
          name="starting_balance"
        >
          <div :class="{ 'has-error': errors[0] }">
            <label class="control-label required">Starting balance</label>
            <div class="input-group">
              <AppInput
                v-model="timeOffPolicy.accrual_policy.starting_balance"
                placeholder="Enter amount of hours"
              />
            </div>
            <span class="help-block">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </div>
      <div class="flex-50 mb-8"></div>
    </div>
    <div
      v-if="showMaxAccrualAmount"
      class="row"
    >
      <div class="col-md-6">
        <ValidationProvider
          v-slot="{ errors }"
          mode="eager"
          rules="required"
          name="maximum_to_accrue"
        >
          <div :class="{ 'has-error': errors[0] }">
            <label class="control-label required">Maximum accrual amount*</label>
            <div class="input-group">
              <AppInput
                v-model="timeOffPolicy.accrual_policy.hours_per_year"
                data-testid="policy-max-accrual-input"
              />
              <span class="input-group-addon">hours per year</span>
            </div>
            <span class="help-block">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </div>
      <div class="flex-50 mb-8"></div>
    </div>
    <div class="row">
      <div class="col-md-6 mb-8">
        <ValidationProvider
          v-slot="{ errors }"
          mode="eager"
          rules="required"
          name="allow_negative_balances"
          :model-value="timeOffPolicy.allow_negative_balances"
        >
          <div :class="{ 'has-error': errors[0] }">
            <label class="control-label required">Allow negative balances</label>
            <AppSelect
              v-model="timeOffPolicy.allow_negative_balances"
              :options="dropdownOptions.allow_negative_balances"
              :select2-options="select2Options"
            />
            <span class="help-block">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </div>
      <div class="col-md-6 mb-8">
        <ValidationProvider
          v-slot="{ errors }"
          mode="eager"
          rules="required"
          name="requires_approval"
          :model-value="timeOffPolicy.requires_approval"
        >
          <div :class="{ 'has-error': errors[0] }">
            <label class="control-label required">Requires approval</label>
            <AppSelect
              v-model="timeOffPolicy.requires_approval"
              :options="dropdownOptions.requires_approval"
              :select2-options="select2Options"
            />
            <span class="help-block">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </div>
    </div>
    <div class="row">
      <div class="col-md-6">
        <ValidationProvider
          v-slot="{ errors }"
          mode="eager"
          rules="required"
          name="balance_rolls_over_annually"
          :model-value="timeOffPolicy.balance_rolls_over_annually"
        >
          <div :class="{ 'has-error': errors[0] }">
            <label class="control-label required">Balance rolls over annually</label>
            <AppSelect
              v-model="timeOffPolicy.balance_rolls_over_annually"
              :options="dropdownOptions.balance_rolls_over_annually"
              :select2-options="select2Options"
            />
            <span class="help-block">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </div>
      <div class="col-md-6">
        <ValidationProvider
          v-slot="{ errors }"
          mode="eager"
          rules="required"
          name="paid"
          :model-value="timeOffPolicy.paid"
        >
          <div :class="{ 'has-error': errors[0] }">
            <label class="control-label required">Paid or unpaid</label>
            <AppSelect
              v-model="timeOffPolicy.paid"
              :options="dropdownOptions.paid"
              :select2-options="select2Options"
            />
            <span class="help-block">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </div>
    </div>
  </div>
</template>

<script>
import { extend } from '~/utils/vee_validate_compat';
import { isEmpty } from 'lodash-es';
import { min, max, between } from '@vee-validate/rules';

extend('min', {
  ...min,
  message: 'is too short (minimum is three characters)',
});

extend('max', {
  ...max,
  message: 'is too long (maximum is fifty characters)',
});

extend('between_or_null', {
  ...between,
  validate: (value, { min, max }) => {
    // Blank (undefined/null/'') skips the range check — vee-validate v4 fields start undefined.
    if (isEmpty(value)) {
      return true;
    }

    return Number(value) >= min && Number(value) <= max;
  },
  params: ['min', 'max'],
  message: 'must be between {min} and {max}',
});

export default {
  name: 'TimeOffPolicySetupForm',
  props: {
    timeOffPolicy: {
      type: Object,
      required: true,
    },
    orgTimeZone: {
      type: String,
      default: 'UTC',
    },
    dropdownOptions: {
      type: Object,
      required: true,
    },
    showDialog: {
      type: Boolean,
      required: true,
    },
    editing: {
      type: Boolean,
      required: true,
    },
    viewMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      select2Options: Object.freeze({
        allowClear: false,
        multiple: false,
        width: '100%',
      }),
      showMaxAccrualAmount: false,
      showAmountAccrued: false,
      tooltipOptions: {
        placement: 'right',
      },
      tooltipText: 'Assign members in the most convenient way to set up a policy',
      totalHoursPerYear: '8760',
    };
  },
  computed: {
    accrualTypeId() {
      return Number(this.timeOffPolicy.accrual_type);
    },
    accrualType() {
      return this.dropdownOptions.accrual_type.find((option) => option.id === this.accrualTypeId)
        .text;
    },
    hasAccrualScheduleWarning() {
      const type = Number(this.timeOffPolicy.accrual_type);

      return (type === 1 || type === 3);
    },
    betweenOrNullRule() {
      return `between_or_null:0,${this.totalHoursPerYear}`;
    },
  },
  created() {
    this.setSubfieldsVisibility();
  },
  methods: {
    setSubfieldsVisibility() {
      this.showMaxAccrualAmount = this.timeOffPolicy.accrual_type !== '0';
      this.showAmountAccrued = this.timeOffPolicy.accrual_type === '1';
    },
  },
};
</script>
